import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { RadioGroup } from "components/Form/components/RadioGroup/RadioGroup";
import { ImageIconComponent, imageProps } from "components/ImageIcon/ImageIcon";
import RecordTable from "components/Table/RecordTable";
import { TableColumn } from "components/Table/tableUtils";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import { modifiedAtDto } from "config/utils";
import { UncommittedData } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { ResourceType } from "graphql/generated/graphql";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";
import style from "../CommitAction.module.scss";

interface UncommittedResourceListProps extends FormComponentProps {
    uncommittedRecordList: UncommittedData[];
    shouldResetSelection?: boolean;
    allRecordsSelected?: boolean;
    setAllRecordsSelected?: (value) => void;
}

export const UncommittedResourceListField = ({
    uncommittedRecordList,
    shouldResetSelection,
    allRecordsSelected,
    setAllRecordsSelected,
    ...props
}: UncommittedResourceListProps) => {
    const {
        // eslint-disable-next-line react/prop-types
        input: { onChange },
        // eslint-disable-next-line react/prop-types
        fieldAttr: { disabled },
    } = props;

    const columns: TableColumn[] = [
        {
            label: "Name",
            key: "name",
            shown: true,
            sortable: true,
            filterable: false,
            dto: ({ name, record_type, record_id }: UncommittedData) => {
                const link = record_id
                    ? getRelativeUrl(getTypedLowerCase(record_type as ResourceType), record_id)
                    : null;
                const content = link ? (
                    <a href={link} rel="noopener noreferrer" className="label" target="_blank">
                        {name}
                    </a>
                ) : (
                    name
                );

                const recordTypeLower = getTypedLowerCase(record_type as ResourceType);

                return (
                    <div className={style.recordNameContainer}>
                        <ImageIconComponent
                            iconByType={recordTypeLower}
                            className={style.image}
                            {...imageProps(recordTypeLower, record_id)}
                        />
                        <div className={style.nameField}>
                            <OverflowTooltip overlay={content} overlayStyle={{ zIndex: 3000 }}>
                                {content}
                            </OverflowTooltip>
                        </div>
                    </div>
                );
            },
        },
        {
            label: "Modified at",
            key: "timestamp",
            dto: ({ timestamp }) => modifiedAtDto({ modified_at: timestamp }),
            shown: true,
            sortable: true,
            filterable: false,
        },
    ];

    return (
        <div className={style.uncommittedListField}>
            <div className={style.tableHeader}>
                {allRecordsSelected === undefined ? (
                    "Please select which resources you want to reset to their status in git:"
                ) : (
                    <RadioGroup
                        options={[
                            { value: "true", label: "Commit all uncommitted changes" },
                            {
                                value: "false",
                                label: "Select uncommitted changes",
                            },
                        ]}
                        input={{
                            value: allRecordsSelected.toString(),
                            onChange: (e) => setAllRecordsSelected?.(e.target.value === "true"),
                        }}
                        identifier="generatedMessage"
                    />
                )}
            </div>
            <div className={`${style.tableWrapper} ${disabled ? style.tableWrapperDisabled : ""}`}>
                <RecordTable
                    columns={columns}
                    data={uncommittedRecordList as any}
                    totalItemCount={uncommittedRecordList.length}
                    options={{
                        toolbar: "custom",
                        toolbarFilters: false,
                        showSearch: false,
                        viewColumns: false,
                        tableBodyMaxHeight: "355px",
                        selectableRows: allRecordsSelected ? "none" : undefined,
                    }}
                    initialQuery={{ limit: 100, order: "+name" }}
                    passSelectedItemsToParent={(selectedItems) => onChange(selectedItems)}
                    recordTypes="all"
                    isLoading={false}
                    shouldResetSelection={shouldResetSelection}
                />
            </div>
        </div>
    );
};
